/**
 * lib/business.tsx
 * ------------------------------------------------------------------
 * Live business info. Starts from the SITE defaults and overlays the
 * Firestore `settings/business` document (publicly readable) so the
 * Phase-2 Settings page can reconfigure contact details across the whole
 * site — and the same document drives the Cloud Functions emails — with
 * no code change and no redeploy.
 *
 * Falls back silently to defaults when offline or before the doc exists.
 * ------------------------------------------------------------------
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { SITE, type Business } from '@/data/site';

const ALLOWED_KEYS: (keyof Business)[] = [
  'name', 'legalName', 'wordmark', 'tagline', 'subTagline', 'city', 'country',
  'address', 'phone', 'phoneDial', 'whatsapp', 'contactEmail', 'bookingsEmail',
  'adminEmail', 'hours', 'social',
];

function mergeSettings(base: Business, data: Record<string, unknown>): Business {
  const next = { ...base } as Business;
  const target = next as unknown as Record<string, unknown>;
  for (const key of ALLOWED_KEYS) {
    const v = data[key];
    if (v !== undefined && v !== null && v !== '') {
      target[key] = v; // shallow-assign known keys only
    }
  }
  return next;
}

const BusinessContext = createContext<Business>(SITE);

export function BusinessProvider({ children }: { children: ReactNode }) {
  const [business, setBusiness] = useState<Business>(SITE);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'business'));
        if (alive && snap.exists()) {
          setBusiness((prev) => mergeSettings(prev, snap.data() as Record<string, unknown>));
        }
      } catch {
        /* no doc yet / offline — keep defaults */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return <BusinessContext.Provider value={business}>{children}</BusinessContext.Provider>;
}

/** Live, settings-aware business info. Use this instead of importing SITE. */
export function useBusiness(): Business {
  return useContext(BusinessContext);
}
